import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import axios from "axios";
import { Loader2, Scale, TrendingUp, TrendingDown, Minus, Newspaper, Megaphone, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LINE_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-6)", "var(--plum)"];

const partyPill = (p) =>
  p === "Democratic" ? "text-sky-300 border-sky-400/30 bg-sky-400/10"
    : p === "Republican" ? "text-rose-300 border-rose-400/30 bg-rose-400/10"
    : "text-amber-300 border-amber-400/30 bg-amber-400/10";

const MomIcon = ({ d }) => d === "rising" ? <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
  : d === "falling" ? <TrendingDown className="w-3.5 h-3.5 text-rose-300" /> : <Minus className="w-3.5 h-3.5 text-slate-500" />;

const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-white/10 bg-white/[0.03] p-5 ${className}`} {...rest}>{children}</div>
);

export default function SharedRace() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/race/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "This shared race link is invalid or has been disabled"));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6" data-testid="shared-race-error">
      <div className="text-center max-w-sm">
        <div className="font-display text-2xl font-bold mb-2">Link unavailable</div>
        <p className="text-sm text-slate-400">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-app text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const c = data.candidate;
  const names = data.polling.averages.map(a => a.name);
  const mine = data.polling.averages.find(a => a.is_mine);

  return (
    <div className="min-h-screen bg-app text-white" data-testid="shared-race-page">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-bold tracking-tight">TotalReach <span className="brand-gradient">Political Intelligence™</span></div>
        <span className="text-[10px] px-2.5 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 uppercase tracking-wider" data-testid="shared-badge">Shared read-only view</span>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="shared-candidate-name">{c.candidate_name}</h1>
            <span className={`text-[11px] px-2.5 py-1 rounded-full border ${partyPill(c.party)}`}>{c.party}</span>
          </div>
          <div className="mt-1.5 text-sm text-slate-400">
            {c.office || "Race"} · {c.state}{c.district && c.district !== "00" ? `-${c.district}` : ""} · Election {c.election_date}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Panel data-testid="shared-stat-avg">
            <div className="text-xs uppercase tracking-wider text-slate-400">Polling average</div>
            <div className="mt-2 font-display text-3xl font-bold text-cyan-300">{mine?.avg != null ? `${mine.avg}%` : "—"}</div>
          </Panel>
          <Panel>
            <div className="text-xs uppercase tracking-wider text-slate-400">Margin</div>
            <div className={`mt-2 font-display text-3xl font-bold ${data.polling.margin > 0 ? "text-emerald-300" : data.polling.margin < 0 ? "text-rose-300" : "text-white"}`}>
              {data.polling.margin != null ? `${data.polling.margin > 0 ? "+" : ""}${data.polling.margin}` : "—"}
            </div>
          </Panel>
          <Panel>
            <div className="text-xs uppercase tracking-wider text-slate-400">Polls tracked</div>
            <div className="mt-2 font-display text-3xl font-bold">{data.polling.poll_count}</div>
          </Panel>
          <Panel>
            <div className="text-xs uppercase tracking-wider text-slate-400">National environment</div>
            <div className="mt-2 text-sm">
              {data.generic.latest_avg ? (
                <><span className="text-blue-300 font-bold">D {data.generic.latest_avg.dem}%</span>
                  <span className="text-slate-500 mx-1.5">/</span>
                  <span className="text-rose-300 font-bold">R {data.generic.latest_avg.rep}%</span>
                  <div className="text-[10px] text-slate-500 mt-1">Generic ballot · VoteHub</div></>
              ) : <span className="text-slate-500">—</span>}
            </div>
          </Panel>
        </div>

        {data.polling.trendline.length > 0 && (
          <Panel data-testid="shared-trendline">
            <h3 className="font-display text-lg font-bold mb-3">Polling trendline</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <LineChart data={data.polling.trendline}>
                  <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" />
                  <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 10 }} tickFormatter={v => (v || "").slice(5)} />
                  <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} unit="%" domain={["dataMin - 5", "dataMax + 5"]} />
                  <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {names.map((n, i) => (
                    <Line key={n} type="monotone" dataKey={n} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={i === 0 ? 2.5 : 1.5} dot={{ r: 2 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">{data.polling.method_note}</div>
          </Panel>
        )}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shared-opposition">
          {data.opposition.map(col => (
            <Panel key={col.name} className={col.is_mine ? "border-cyan-400/30" : ""}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-bold">{col.name} {col.is_mine && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 uppercase ml-1">Candidate</span>}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border inline-block mt-1 ${partyPill(col.party)}`}>{col.party}</span>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-bold">{col.avg != null ? `${col.avg}%` : "—"}</div>
                  {col.momentum && <div className="text-xs text-slate-400 inline-flex items-center gap-1"><MomIcon d={col.momentum.direction} /> {col.momentum.delta > 0 ? "+" : ""}{col.momentum.delta}</div>}
                </div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Megaphone className="w-3 h-3" /> Live ads (AdLibrary)</div>
                {(col.live_ads || []).length === 0 ? <div className="text-xs text-slate-600">No live ads detected.</div> : (
                  <div className="space-y-1.5">
                    {col.live_ads.slice(0, 3).map(ad => (
                      <div key={ad.ad_key} className="rounded-md bg-white/[0.03] border border-white/5 p-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-300 truncate">{ad.advertiser}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-slate-400 uppercase shrink-0">{ad.platform}</span>
                        </div>
                        {ad.message && <div className="text-slate-500 truncate mt-0.5">{ad.message}</div>}
                        <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> {Number(ad.impressions || 0).toLocaleString()} imp{ad.days_running ? ` · ${ad.days_running}d running` : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {data.issues.length > 0 && (
            <Panel data-testid="shared-issues">
              <h3 className="font-display text-lg font-bold mb-3">Public issue landscape</h3>
              <div className="space-y-3">
                {data.issues.slice(0, 6).map(i => (
                  <div key={i.issue}>
                    <div className="flex justify-between text-sm"><span className="text-slate-200">{i.issue}</span>
                      <span className={i.latest_net_sentiment >= 0 ? "text-emerald-300 text-xs" : "text-rose-300 text-xs"}>Net {i.latest_net_sentiment > 0 ? "+" : ""}{i.latest_net_sentiment}</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 mt-1"><div className="h-full rounded-full bg-cyan-400/70" style={{ width: `${i.latest_salience}%` }} /></div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {data.news.length > 0 && (
            <Panel data-testid="shared-news">
              <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Newspaper className="w-4 h-4 text-amber-300" /> Race news</h3>
              <div className="space-y-2.5">
                {data.news.map(n => (
                  <a key={n.key} href={n.link} target="_blank" rel="noreferrer" className="block group">
                    <div className="text-sm text-slate-300 group-hover:text-cyan-300 leading-snug">{n.title}</div>
                    <div className="text-[10px] text-slate-600">{n.source}{n.published_at ? ` · ${n.published_at.slice(0, 10)}` : ""}</div>
                  </a>
                ))}
              </div>
            </Panel>
          )}
        </div>

        <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-4 flex items-start gap-3" data-testid="shared-measurement-note">
          <Scale className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200/85 leading-relaxed">{data.measurement_note}</p>
        </div>

        <footer className="text-center text-xs text-slate-600 pb-8">
          Powered by <span className="text-slate-400">TotalReach Political Intelligence™</span> · totalreach.us
        </footer>
      </main>
    </div>
  );
}
